import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wallet, ExternalLink, Loader2, Save, Shield, History, AlertTriangle } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const ADMIN_EMAIL = 'yhuan@corpcraftlegal.org';

function short(addr) {
  if (!addr || addr.length < 10) return addr || '';
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}
function PolygonLink({ addr }) {
  if (!addr) return null;
  return (
    <a
      href={`https://polygonscan.com/address/${addr}`}
      target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300"
      data-testid={`polygonscan-link-${addr.slice(2, 8)}`}
    >
      <ExternalLink className="w-3 h-3" /> View on PolygonScan
    </a>
  );
}

export default function WalletSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.email || '').toLowerCase() === ADMIN_EMAIL;

  // Client wallet
  const [myAddr, setMyAddr] = useState('');
  const [originalMyAddr, setOriginalMyAddr] = useState('');
  const [savingMine, setSavingMine] = useState(false);
  const [myErr, setMyErr] = useState('');

  // Admin platform wallet
  const [platform, setPlatform] = useState(null);
  const [history, setHistory] = useState([]);
  const [newAddr, setNewAddr] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [platformErr, setPlatformErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const mine = await api.get('/me/wallet');
        setMyAddr(mine.data.wallet_address || '');
        setOriginalMyAddr(mine.data.wallet_address || '');
        if (isAdmin) {
          const pw = await api.get('/admin/platform-wallet');
          setPlatform(pw.data.current);
          setHistory(pw.data.history || []);
          setNewAddr(pw.data.current?.address || '');
          setNewLabel(pw.data.current?.label || '');
        } else {
          const pw = await api.get('/platform-wallet');
          setPlatform(pw.data);
        }
      } catch (e) {
        console.error('Wallet load failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin]);

  const saveMine = async (e) => {
    e.preventDefault();
    setMyErr('');
    setSavingMine(true);
    try {
      const trimmed = myAddr.trim();
      await api.put('/me/wallet', { wallet_address: trimmed || null });
      toast.success(trimmed ? 'Wallet saved' : 'Wallet cleared');
      setOriginalMyAddr(trimmed);
    } catch (err) {
      setMyErr(err.response?.data?.detail || 'Could not save wallet');
    } finally {
      setSavingMine(false);
    }
  };

  const savePlatform = async (e) => {
    e.preventDefault();
    setPlatformErr('');
    setSavingPlatform(true);
    try {
      const { data } = await api.put('/admin/platform-wallet', {
        address: newAddr.trim(),
        label: newLabel.trim(),
      });
      setPlatform(data.current);
      // Reload history
      const pw = await api.get('/admin/platform-wallet');
      setHistory(pw.data.history || []);
      toast.success('Platform wallet updated');
    } catch (err) {
      setPlatformErr(err.response?.data?.detail || 'Could not update platform wallet');
    } finally {
      setSavingPlatform(false);
    }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="wallet-settings-page">
        <button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-600" data-testid="wallets-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-blue-500" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold font-['Outfit']">Wallets</h1>
          </div>
          <p className="text-slate-400 text-sm max-w-2xl">
            Your saved Polygon address auto-fills on every USDC checkout.
            {isAdmin && ' As admin, you can also rotate the platform receive address below.'}
          </p>
        </div>

        {/* ---------- Client wallet ---------- */}
        <form onSubmit={saveMine} className="stat-card space-y-4" data-testid="my-wallet-card">
          <div>
            <h2 className="text-lg font-semibold">Your wallet</h2>
            <p className="text-sm text-slate-400">
              Enter the Polygon address you'll send USDC from. Saved once, auto-fills on every future checkout.
            </p>
          </div>

          <div>
            <Label htmlFor="my-wallet">Polygon address</Label>
            <Input
              id="my-wallet"
              value={myAddr}
              onChange={(e) => setMyAddr(e.target.value)}
              placeholder="0x…"
              className="font-mono text-sm"
              data-testid="my-wallet-input"
            />
            {myAddr && (
              <div className="mt-1 text-xs text-slate-500 flex items-center gap-2">
                <span className="font-mono">{short(myAddr)}</span>
                <PolygonLink addr={myAddr} />
              </div>
            )}
            {myErr && (
              <div className="mt-2 text-xs text-red-500 flex items-center gap-1" data-testid="my-wallet-error">
                <AlertTriangle className="w-3.5 h-3.5" /> {myErr}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            {originalMyAddr && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setMyAddr('')}
                data-testid="my-wallet-clear-btn"
              >
                Clear
              </Button>
            )}
            <Button
              type="submit"
              disabled={savingMine || myAddr === originalMyAddr}
              className="gradient-accent"
              data-testid="my-wallet-save-btn"
            >
              {savingMine ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </form>

        {/* ---------- Platform wallet (public view for everyone) ---------- */}
        {!isAdmin && platform && (
          <div className="stat-card" data-testid="platform-wallet-public">
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4 text-slate-500" /> CorpCraft receive address
            </h2>
            <p className="text-xs text-slate-400 mb-3">
              {platform.label || 'Where your USDC checkout payments are received'}
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 font-mono text-sm break-all" data-testid="platform-wallet-value">
              {platform.address}
            </div>
            <div className="mt-2"><PolygonLink addr={platform.address} /></div>
          </div>
        )}

        {/* ---------- Admin platform-wallet management ---------- */}
        {isAdmin && (
          <form onSubmit={savePlatform} className="stat-card space-y-4" data-testid="admin-platform-wallet-card">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" /> Platform receive address <span className="ml-2 text-xs bg-blue-500/10 text-blue-700 px-2 py-0.5 rounded">Admin</span>
              </h2>
              <p className="text-sm text-slate-400">
                The Polygon address every client sees on crypto checkout. Change takes effect on the next cart view.
              </p>
            </div>

            {platform && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Current</div>
                <div className="font-mono text-sm break-all" data-testid="admin-current-wallet">{platform.address}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {platform.label || '—'} · updated {platform.updated_at ? new Date(platform.updated_at).toLocaleString() : '—'}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="new-addr">New Polygon address</Label>
              <Input
                id="new-addr"
                value={newAddr}
                onChange={(e) => setNewAddr(e.target.value)}
                placeholder="0x…"
                className="font-mono text-sm"
                data-testid="admin-new-wallet-input"
              />
            </div>

            <div>
              <Label htmlFor="new-label">Label (optional)</Label>
              <Input
                id="new-label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g. CorpCraft Treasury — Prod"
                maxLength={120}
                data-testid="admin-new-label-input"
              />
            </div>

            {platformErr && (
              <div className="text-xs text-red-500 flex items-center gap-1" data-testid="admin-wallet-error">
                <AlertTriangle className="w-3.5 h-3.5" /> {platformErr}
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" disabled={savingPlatform || !newAddr.trim()} className="gradient-accent" data-testid="admin-save-platform-wallet-btn">
                {savingPlatform ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save new address
              </Button>
            </div>

            {history.length > 0 && (
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-500" /> Change history ({history.length})
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="admin-wallet-history-table">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-slate-200">
                        <th className="py-2 pr-3">When</th>
                        <th className="py-2 pr-3">Previous</th>
                        <th className="py-2 pr-3">New</th>
                        <th className="py-2">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-2 pr-3 text-slate-500">{new Date(h.changed_at).toLocaleString()}</td>
                          <td className="py-2 pr-3 font-mono">{h.previous_address ? short(h.previous_address) : '—'}</td>
                          <td className="py-2 pr-3 font-mono">{short(h.new_address)}</td>
                          <td className="py-2 text-slate-500">{h.changed_by === 'system-seed' ? 'seed' : h.changed_by.slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}
